import styled from "@emotion/styled"
import {
  BORDER_GREY_COLOR,
  DARK_BLUE_COLOR,
  DARKER_GREY_COLOR,
  LIGHT_GREY_COLOR,
  LIGHTER_GREY_COLOR,
  PX12,
  PX14,
  PX18,
  PX24,
  PX28,
  PX32,
  SLIGHTLY_LIGHTER_GREY_COLOR,
} from "theme/consts"

import { Value } from "@stringtale/react"
import { SupportTabs } from "app/support/components/SupportPopover"
import { SetStateAction, useAtom } from "jotai"
import { Dispatch, useRef, useState, useEffect, useMemo } from "react"
import { poppins } from "theme/fonts"
import Button from "ui/Button"
import Group from "ui/Group"
import Stack from "ui/Stack"
import Text from "ui/Text"
import Title from "ui/Title"
import { GetSupportButtonContentResult } from "../queries/getSupportButtonContent"
import { RichText } from "app/admin/components/RichText"
import { discoveryModalOpenAtom } from "../discoveryModalState"

import { GetCurrentUserResult } from "app/users/queries/getCurrentUser"
import { usePosthogWithCurrentUser } from "app/core/hooks/usePosthogWithCurrentUser"
import useFilePath from "app/files/hooks/useFilePath"

import { LargePlayButton as LargePlayButtonBase } from "app/player/components/LargePlayButton"
import { Dialog } from "radix-ui"
import SupportVideo from "./SupportVideo"

import RoundedButton from "ui/RoundedButton"
import { FaArrowUp } from "@react-icons/all-files/fa/FaArrowUp"
import SupportScrollArea from "./SupportStyledScrollArea"
import Image from "app/files/components/Image"
import { FaChevronRight } from "@react-icons/all-files/fa/FaChevronRight"
import Link from "ui/Link"
import startViewTransition from "app/core/utils/startViewTransition"
import CardLabel from "ui/CardLabel"

const SupportContentTitle = styled(Title)`
  margin-top: 25px;
  font-family: ${poppins.style.fontFamily};
  line-height: ${PX32};
`

const SupportContentContainer = styled.div`
  height: 100%;
  position: relative;
  width: 100%;
  box-sizing: border-box;
`

const SupportContentBackground = styled(Stack)<{
  backgroundColor: string | null
}>`
  background-color: ${({ backgroundColor }) => backgroundColor || "#305CDE"};
  flex: 1;
  border-radius: 30px 30px 0 0;
  height: calc(100% - 40px);
  position: absolute;
  flex-grow: 0;
  top: 0;
  left: 0;
  padding: 0 30px;
`

const SupportContentImage = styled.img<{ marginTop: number }>`
  margin-top: ${({ marginTop }) => marginTop}px;
  z-index: 1;
`

const SupportContentDrawer = styled(Stack)`
  background-color: white;
  margin-top: -30px;
  border-radius: 30px 30px 0 0;
  flex: 1;
  padding: 32px 24px;
  min-height: 166px;
  font-family: ${poppins.style.fontFamily};
  font-size: ${PX14};
  line-height: ${PX24};
  z-index: 10;
  position: relative;
`

const ButtonOnlyContainer = styled(Stack)`
  height: 80px;
  min-height: 80px;
  max-height: 80px;
  width: 100%;
  background-color: white;
  border-radius: 30px;
  box-sizing: border-box;
  flex-grow: 0;
  flex-shrink: 0;
`

const SupportButtonGroup = styled(Group)`
  gap: 10px;
  width: 100%;
  padding: 6px 12px;
  box-sizing: border-box;
  height: 100%;
  flex-shrink: 0;
  flex-grow: 0;
  & > * {
    flex: 1;
    width: 0;
  }
`

const SupportButtonBackground = styled(Button)`
  border-radius: 22px;
  background-color: #eee;
  flex-grow: 1;
  padding: 6px;
  box-sizing: border-box;
  height: 100%;
  flex-shrink: 0;

  &:hover {
    background-color: #e0e0e0;
  }
`

const SupportButtonText = styled(Text)`
  font-family: ${poppins.style.fontFamily};
  padding: 0;
  margin: 0;
  line-height: ${PX14};
`

const SupportButtonDrawerContainer = styled(Stack)`
  height: 100%;
  width: 100%;
  position: absolute;
  z-index: 5;
  top: 0;
  left: 0;
  border-radius: 30px 30px 0 0;
  overflow: hidden;
`

const SupportButtonsBottomContainer = styled.div`
  background-color: white;
  align-self: flex-end;
  width: 100%;
  box-sizing: border-box;
  border-radius: 0 0 30px 30px;
  border-top: 1px solid ${LIGHT_GREY_COLOR};
  height: 80px;
`

const SupportTitle = styled(Title)`
  font-family: ${poppins.style.fontFamily};
`

function SupportButton({
  icon,
  label,
  onClick,
}: {
  icon: string
  label: React.ReactNode
  onClick: () => void
}) {
  return (
    <SupportButtonBackground type="button" onClick={onClick}>
      <Stack gap="2px">
        <img src={icon} height={36} />
        <SupportButtonText size={PX12} bold>
          {label}
        </SupportButtonText>
      </Stack>
    </SupportButtonBackground>
  )
}

function SupportButtons({
  setCurrentTab,
  currentUser,
  setIsSupportButtonOpen,
}: {
  setCurrentTab: Dispatch<SetStateAction<SupportTabs>>
  currentUser: GetCurrentUserResult
  setIsSupportButtonOpen: Dispatch<SetStateAction<boolean>>
}) {
  const [discoveryModalOpen, setDiscoveryModalOpen] = useAtom(
    discoveryModalOpenAtom
  )
  const analytics = usePosthogWithCurrentUser(currentUser)
  return (
    <SupportButtonGroup gap="10px" justify="stretch" align="center">
      <SupportButton
        icon={"/icons/support/support.png"}
        label={
          <Value name="apps.web.src.support.components.dynamicsupportcontent.support">
            Ondersteuning
          </Value>
        }
        onClick={() => setCurrentTab("staticSupportLinks")}
      />
      <SupportButton
        icon={"/icons/support/discovery-superhero-no-flash.png"}
        label={
          <Value name="apps.web.src.support.components.dynamicsupportcontent.ontdek_123zing">
            Ontdek 123ZING
          </Value>
        }
        onClick={() => {
          analytics.capture({
            name: "discovery_modal_opened",
          })
          setIsSupportButtonOpen(false)
          startViewTransition(() => {
            setDiscoveryModalOpen(true)
          })
        }}
      />
    </SupportButtonGroup>
  )
}

function NoSupportContentMinimalView({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <ButtonOnlyContainer align="center" justify="center">
      {children}
    </ButtonOnlyContainer>
  )
}

const VideoStyling = styled.div`
  width: inherit;
  height: inherit;
  border-radius: inherit;
  background-color: white;
  border: 1px solid ${DARK_BLUE_COLOR};
  box-sizing: border-box;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  position: relative;
`

const VideoHoverEffect = styled.div`
  width: 289px;
  height: 168px;
  border-radius: inherit;
  background-color: #83bcff;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%) rotate(0deg);
  z-index: 2;
  transition: transform 0.1s ease-out;
`

const Shimmer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    110deg,
    transparent 0%,
    transparent 20%,
    rgba(255, 255, 255, 0.7) 20%,
    rgba(255, 255, 255, 1) 55%,
    transparent 55%,
    transparent 60%,
    rgba(255, 255, 255, 1) 60%,
    rgba(255, 255, 255, 0.7) 75%,
    transparent 75%,
    transparent 100%
  );
  transform: translateX(0%) skewX(-45deg);
  pointer-events: none;
  opacity: 0.2;
  transition: 0.1s ease-out;
`

const LargePlayButton = styled(LargePlayButtonBase)`
  border: 1px solid ${DARK_BLUE_COLOR};
  box-sizing: border-box;
  transition: transform 0.1s ease-out;
`

const SupportVideoContainer = styled.div<{ marginTop: number }>`
  width: 289px;
  height: 168px;
  border-radius: 27px;
  margin-top: ${({ marginTop }) => marginTop}px;
  cursor: pointer;

  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);

  ${VideoStyling} {
    width: 100%;
    height: 100%;
    border: 1px solid ${DARK_BLUE_COLOR};
    border-radius: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    position: relative;
    z-index: 3;
    transform: scale(1);
    transition: transform 0.1s ease-out;
    & > div {
      border: 1px solid ${DARK_BLUE_COLOR};
      border-radius: 22px;
      width: 100%;
      height: 100%;
      box-sizing: border-box;
    }
  }

  &:hover {
    ${VideoHoverEffect} {
      transform: translateX(-50%) rotate(-5deg);
    }
    ${VideoStyling} {
      transform: scale(1.03);
    }
    ${Shimmer} {
      opacity: 0.05;
      transform: translateX(5%) skewX(-45deg);
    }
    ${LargePlayButton} {
      transform: scale(1.03);
    }
  }
`

const StyledButton = styled(RoundedButton)`
  border-radius: 22px;
  height: auto;
  width: 100%;
  background-color: ${SLIGHTLY_LIGHTER_GREY_COLOR};
  color: ${DARK_BLUE_COLOR};
  padding: 6px;
  text-align: start;
`

const LinkItem = styled(Link)`
  border-radius: 22px;
  width: 100%;
  height: auto;
  background-color: ${SLIGHTLY_LIGHTER_GREY_COLOR};
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  padding: 6px;
  box-sizing: border-box;
  flex-wrap: nowrap;

  &:hover {
    background-color: ${LIGHT_GREY_COLOR};
  }
`

const LinkItemContent = styled(Group)`
  width: 100%;
`

const ArrowContainer = styled.div`
  width: 16px;
  flex-shrink: 0;
  /* background-color: red; */
`

const ImagePlaceholder = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 18px;
  background-color: ${BORDER_GREY_COLOR};
  flex-grow: 0;
  flex-shrink: 0;
`

const UpButton = styled(StyledButton)`
  padding: 12px;
  background-color: ${SLIGHTLY_LIGHTER_GREY_COLOR};

  &:hover {
    background-color: ${LIGHT_GREY_COLOR};
  }
`
const VideoCardLabel = styled(CardLabel)`
  margin-top: 20px;
  font-family: ${poppins.style.fontFamily};
`

export default function DynamicSupportContent({
  supportContent,
  setCurrentTab,
  currentUser,
  setIsSupportButtonOpen,
}: {
  supportContent?: GetSupportButtonContentResult
  setCurrentTab: Dispatch<SetStateAction<SupportTabs>>
  currentUser: GetCurrentUserResult
  setIsSupportButtonOpen: Dispatch<SetStateAction<boolean>>
}) {
  const [isSupportVideoOpen, setIsSupportVideoOpen] = useState(false)
  const [videoMarginTop, setVideoMarginTop] = useState(-30)
  const [imageMarginTop, setImageMarginTop] = useState(-80)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const animationFrameRef = useRef<number | undefined>(undefined)
  const imageSrc = useFilePath(supportContent?.image, {
    height: 226,
    width: 226,
    fit: "cover",
  })

  const mediaType: "video" | "image" | "none" = useMemo(() => {
    if (supportContent?.video) {
      return "video"
    } else if (supportContent?.image) {
      return "image"
    }
    return "none"
  }, [supportContent])

  useEffect(() => {
    const handleScroll = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          const scrollTop = scrollContainerRef.current.scrollTop
          if (mediaType === "video") {
            const scrollAdjustedMargin = (scrollTop - 260) / 4
            const newVideoMarginTop =
              scrollAdjustedMargin > 0
                ? 0
                : scrollAdjustedMargin < -30
                  ? -30
                  : scrollAdjustedMargin
            setVideoMarginTop(newVideoMarginTop)
          }
          if (mediaType === "image") {
            const scrollAdjustedMargin = (scrollTop - 260) / 2
            const newImageMarginTop =
              scrollAdjustedMargin > 0
                ? 0
                : scrollAdjustedMargin < -80
                  ? -80
                  : scrollAdjustedMargin
            setImageMarginTop(newImageMarginTop)
          }
        }
      })
    }

    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll)
      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll)
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
        }
      }
    }
  }, [mediaType])

  if (!supportContent) {
    // Return bottom bar with buttons
    return (
      <NoSupportContentMinimalView>
        <SupportButtons
          setCurrentTab={setCurrentTab}
          currentUser={currentUser}
          setIsSupportButtonOpen={setIsSupportButtonOpen}
        />
      </NoSupportContentMinimalView>
    )
  }

  return (
    <SupportContentContainer>
      <Dialog.Root
        open={isSupportVideoOpen}
        onOpenChange={setIsSupportVideoOpen}
      >
        {supportContent.video && isSupportVideoOpen && (
          <SupportVideo
            video={supportContent.video}
            onClose={() => setIsSupportVideoOpen(false)}
          />
        )}
      </Dialog.Root>
      <SupportContentBackground
        align="center"
        justify="flex-start"
        backgroundColor={supportContent.backgroundColor}
      >
        {mediaType === "image" && imageSrc && (
          <SupportContentImage
            src={imageSrc}
            width={226}
            marginTop={imageMarginTop}
          />
        )}
        {(mediaType === "video" || mediaType === "none") && (
          <div style={{ width: 226, height: 146 }} />
        )}
        {supportContent.slogan && (
          <SupportContentTitle size={PX28} weight="700" color="white" center>
            {supportContent.slogan}
          </SupportContentTitle>
        )}
      </SupportContentBackground>

      <SupportButtonDrawerContainer align="flex-end" justify="flex-end">
        <SupportScrollArea ref={scrollContainerRef}>
          <div style={{ paddingTop: "300px" }}>
            {mediaType === "video" && (
              <SupportVideoContainer marginTop={videoMarginTop}>
                <VideoHoverEffect />
                <VideoStyling>
                  <Group
                    onClick={() => setIsSupportVideoOpen(true)}
                    style={{
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    {supportContent.image && (
                      <Image
                        file={supportContent.image}
                        alt={supportContent.displayTitle}
                        width={226}
                        height={146}
                        style={{
                          objectFit: "cover",
                          width: "100%",
                          height: "100%",
                        }}
                      />
                    )}
                    <Shimmer />
                    <LargePlayButton size={56} />
                  </Group>
                </VideoStyling>
                <VideoCardLabel
                  onClick={() => setIsSupportVideoOpen(true)}
                  color={"#fef862"}
                  textColor={DARK_BLUE_COLOR}
                >
                  <Value name="apps.web.src.support.components.dynamicsupportcontent.video">
                    Video
                  </Value>
                </VideoCardLabel>
              </SupportVideoContainer>
            )}
            <SupportContentDrawer align="flex-start" gap="10px">
              <SupportTitle size={PX18} weight="700" color={DARK_BLUE_COLOR}>
                {supportContent.displayTitle}
              </SupportTitle>
              <Stack gap="28px">
                <RichText body={supportContent.content} />
                <Stack gap="48px" align="stretch">
                  {supportContent.linkItems.length > 0 && (
                    <Stack gap="10px" align="stretch">
                      {supportContent.linkItems.map((item) => (
                        <LinkItem
                          href={item.url}
                          key={item.id}
                          target={
                            item.buttonTarget === "BLANK" ? "_blank" : "_self"
                          }
                        >
                          <LinkItemContent
                            align="center"
                            justify="space-between"
                            gap="10px"
                          >
                            <Group gap="10px">
                              {item.image ? (
                                <Image
                                  file={item.image}
                                  alt={item.displayTitle}
                                  width={72}
                                  height={72}
                                  style={{ borderRadius: "18px" }}
                                />
                              ) : (
                                <ImagePlaceholder />
                              )}
                              <Text
                                size={PX14}
                                bold
                                style={{ lineHeight: "18px" }}
                              >
                                {item.displayTitle}
                              </Text>
                            </Group>
                            <ArrowContainer>
                              <FaChevronRight size={12} />
                            </ArrowContainer>
                          </LinkItemContent>
                        </LinkItem>
                      ))}
                    </Stack>
                  )}
                  <UpButton
                    right={<FaArrowUp size={12} />}
                    onClick={() => {
                      scrollContainerRef.current?.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      })
                    }}
                  >
                    <Text size={PX12} style={{ fontWeight: "700" }}>
                      <Value name="apps.web.src.support.components.dynamicsupportcontent.terug_naar_boven">
                        Terug naar boven
                      </Value>
                    </Text>
                  </UpButton>
                </Stack>
              </Stack>
            </SupportContentDrawer>
          </div>
        </SupportScrollArea>
        <SupportButtonsBottomContainer>
          <SupportButtons
            setCurrentTab={setCurrentTab}
            currentUser={currentUser}
            setIsSupportButtonOpen={setIsSupportButtonOpen}
          />
        </SupportButtonsBottomContainer>
      </SupportButtonDrawerContainer>
    </SupportContentContainer>
  )
}
